type ProblemDetails = {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  /** Request validation failures, keyed by property. */
  errors?: Record<string, string[]>
}

type ProblemDetailsErrorCause = string | Record<string, ApiException[]> | undefined

class ProblemDetailsError extends Error {
  public readonly instance: string | undefined
  public readonly fix: string | undefined
  public readonly status: number | undefined
  /** Each validation message the server sent, whichever property it is about. */
  public readonly validationMessages: string[]
  public cause?: ProblemDetailsErrorCause
  constructor(problemDetails: ProblemDetails) {
    super()
    this.message = problemDetails.title
    this.instance = problemDetails.instance
    this.fix = problemDetails.detail
    this.status = problemDetails.status
    this.validationMessages = Object.values(problemDetails.errors ?? {}).flat()
    this.cause = problemDetails.detail
  }
}

class ProblemDetailsErrorConflict extends ProblemDetailsError {
  public cause?: Record<string, ApiException[]> | undefined

  constructor(problemDetails: ProblemDetailsConflict) {
    super(problemDetails)
    this.cause = problemDetails.conflicts
  }
}

type ProblemDetailsConflict = ProblemDetails & {
  conflicts: Record<string, ApiException[]>
}

type ApiException = {
  value?: string
  message?: string
  entities: ExceptionEntity[]
}

type ExceptionEntity = {
  type: string
  id: string
  name?: string
}

/** The clashes a 409 reported for one property, or none when `cause` is not a conflict. */
function conflictsFor(cause: unknown, property: string): ApiException[] {
  if (!(cause instanceof ProblemDetailsErrorConflict)) {
    return []
  }
  return cause.cause?.[property] ?? []
}

function isProblemDetailsConflict(
  responseBody: ProblemDetails | string | undefined | unknown
): responseBody is ProblemDetailsConflict {
  return (
    isProblemDetails(responseBody) &&
    (responseBody as ProblemDetailsConflict).conflicts !== undefined
  )
}

function isProblemDetails(
  responseBody: ProblemDetails | string | undefined | unknown
): responseBody is ProblemDetails {
  return (responseBody as ProblemDetails)?.title !== undefined
}

export {
  type ProblemDetails,
  type ProblemDetailsConflict,
  type ApiException,
  ProblemDetailsError,
  ProblemDetailsErrorConflict,
  conflictsFor,
  isProblemDetails,
  isProblemDetailsConflict,
}
